import {
  BASECODE_MAX_LENGTH,
  CANSIZECODE_MAX_LENGTH,
  COLORCODE_MAX_LENGTH,
  COLORNAME_MAX_LENGTH,
  NUMERIC_ORDER_STATUS,
  ORDER_STATUS_WAITING,
  SOURCE_USER,
} from '../../Constants';
import i18n from '../../localization/i18n';

export const ADLER_END_MARKER = 'End_Code';
export const ADLER_HEADER_FIELD_COUNT = 20;
export const ADLER_MAX_COMPONENTS = 20;
export const ADLER_COMPONENT_FIELD_COUNT = 3;
export const ADLER_TOTAL_FIELD_COUNT =
  ADLER_HEADER_FIELD_COUNT +
  ADLER_MAX_COMPONENTS * ADLER_COMPONENT_FIELD_COUNT +
  1;

const ADLER_MASS_UNITS = ['g', 'kg'];
const ADLER_VOLUME_UNITS = ['ml', 'l'];
const ADLER_UNITS = [...ADLER_MASS_UNITS, ...ADLER_VOLUME_UNITS];

const adlerHeaderFields = [
  'startMarker',
  'date',
  'time',
  'source',
  'zwischenKlasse',
  'colorNumber',
  'colorName',
  'standardName',
  'pigFilePath',
  'pigFileName',
  'frmPath',
  'frmFileName',
  'baseReferenceMode',
  'dosingMode',
  'operationType',
  'productDescription',
  'batchNumber',
  'inspectionOrder',
  'reserve1',
  'reserve2',
];

function adlerError(key, fallback, options) {
  return { msg: i18n.t(key, fallback, options) };
}

function isAdlerMassUnit(unit) {
  return ADLER_MASS_UNITS.includes(unit);
}

function isAdlerVolumeUnit(unit) {
  return ADLER_VOLUME_UNITS.includes(unit);
}

export function isAdlerQrPayload(barcode) {
  if (typeof barcode !== 'string') return false;

  const payload = barcode.trim();
  return (
    payload.startsWith('@;') &&
    payload.toLowerCase().endsWith(`;${ADLER_END_MARKER.toLowerCase()}`)
  );
}

export function parseAdlerQrPayload(barcode) {
  if (!isAdlerQrPayload(barcode)) return null;

  const originalPayload = barcode.trim();
  const fields = originalPayload.split(';').map((field) => field.trim());
  const endMarkerIndex = fields.length - 1;

  const header = adlerHeaderFields.reduce((acc, name, index) => {
    acc[name] = fields[index] || '';
    return acc;
  }, {});

  const components = [];
  const componentFieldsEnd = Math.min(
    ADLER_HEADER_FIELD_COUNT + ADLER_MAX_COMPONENTS * 3,
    endMarkerIndex
  );

  for (
    let fieldIndex = ADLER_HEADER_FIELD_COUNT, slot = 1;
    fieldIndex < componentFieldsEnd && slot <= ADLER_MAX_COMPONENTS;
    fieldIndex += 3, slot++
  ) {
    const name = fields[fieldIndex] || '';
    if (!name) {
      continue;
    }
    const quantityText = fields[fieldIndex + 1] || '';
    const unitFieldIndex =
      fieldIndex + 2 < endMarkerIndex ? fieldIndex + 2 : null;
    const unit = (
      (unitFieldIndex != null && fields[unitFieldIndex]) ||
      ''
    ).toLowerCase();

    const quantityMissing = quantityText === '';
    const unitMissing = !ADLER_UNITS.includes(unit);

    if (quantityMissing) {
      throw adlerError(
        'msg.barcodeComponentQuantityMissing',
        'Component quantity is missing'
      );
    }

    const quantity = Number(quantityText);
    if (!Number.isFinite(quantity)) {
      throw adlerError(
        'msg.barcodeComponentQuantityInvalid',
        'Component quantity is invalid'
      );
    }
    if (unitMissing) {
      throw adlerError(
        'msg.barcodeComponentUnitInvalid',
        'Component unit is invalid or missing'
      );
    }

    components.push({
      slot,
      name,
      quantity,
      quantityText,
      unit,
      isBase: slot === 1,
      fieldIndex,
      quantityFieldIndex: fieldIndex + 1,
      unitFieldIndex,
    });
  }

  if (!components.length) {
    throw adlerError('msg.barcodeNoValidComponent', 'No valid component found');
  }

  const result = {
    originalPayload,
    fields,
    header,
    components,
    endMarkerIndex,
  };
  console.log('Parsed QR:', result);
  return result;
}

function truncate(str, length) {
  return str != null ? String(str).slice(0, length).trim() : null;
}

export function adlerQuantity(value, decimals = 3) {
  if (!Number.isFinite(value)) return null;

  const trimmed = value
    .toFixed(decimals)
    .replace(/(\.\d*?)0+$/, '$1') // trim trailing zeros
    .replace(/\.$/, ''); // trim trailing decimal point
  return trimmed === '-0' ? '0' : trimmed;
}

export function adlerColorantCode(cntcode) {
  return String(cntcode || '')
    .trim()
    .split(/\s+/)[0];
}

export function tintingResultMap(tintingResult) {
  return new Map(
    (tintingResult?.cnts || []).map((cnt) => [
      adlerColorantCode(cnt.cntcode),
      cnt,
    ])
  );
}

function componentQuantityAsVolume(component, specificgravity) {
  if (isAdlerMassUnit(component.unit)) {
    const quantityInGrams =
      component.unit === 'kg' ? component.quantity * 1000 : component.quantity;
    return quantityInGrams / specificgravity; // possibly NaN or Infinity
  }
  if (isAdlerVolumeUnit(component.unit)) {
    return component.unit === 'l'
      ? component.quantity * 1000
      : component.quantity;
  }
  return NaN;
}

function findColorantByCode(cntcodemap, code) {
  const exact = cntcodemap.get(code);
  if (exact) return exact;

  // If no exact match, try to match code against the start of cntcode in the map.
  return Array.from(cntcodemap.values()).find((cnt) =>
    cnt.cntcode.startsWith(code)
  );
}

function findCansizeByBaseVolume(cansizes, baseVolume) {
  if (!Number.isFinite(baseVolume)) return null;

  return (cansizes || []).find(
    (cansize) =>
      cansize.basevolume != null &&
      Math.abs(cansize.basevolume - baseVolume) < 0.1
  );
}

function findProductBaseCan(products, cansizes, baseComponent, productcode) {
  const product = products.find((p) => p.productcode?.trim() === productcode);
  if (!product) return {};

  const base = product.basepaints.find(
    (b) => b.abstractbase?.abasecode?.trim() === baseComponent.name
  );

  const baseVolume = componentQuantityAsVolume(
    baseComponent,
    base?.specificgravity
  );

  const can =
    base?.cans?.find(
      (can) =>
        can.basevolume != null && Math.abs(can.basevolume - baseVolume) < 0.1
    ) || findCansizeByBaseVolume(cansizes, baseVolume);

  return { product, base, can };
}

function adlerColour(adlerQr) {
  const { colorNumber, colorName } = adlerQr.header;
  const colourcode = truncate(
    colorNumber || colorName || i18n.t('lbl.qrFormula', 'QR formula'),
    COLORCODE_MAX_LENGTH
  );
  const colourname = truncate(colorName, COLORNAME_MAX_LENGTH);

  return { colourcode, colourname, originalcode: colorNumber };
}

export function buildAdlerOrderitem(
  adlerQr,
  cntcodemap,
  products,
  cansizes,
  zoneid,
  siteid
) {
  const now = new Date().toISOString();
  const baseComponent = adlerQr.components[0];
  const colorantComponents = adlerQr.components.slice(1);
  const productcode = adlerQr.header.zwischenKlasse;
  const { product, base, can } = findProductBaseCan(
    products,
    cansizes,
    baseComponent,
    productcode
  );
  const baseVolume = componentQuantityAsVolume(
    baseComponent,
    base?.specificgravity
  );
  const productname = truncate(
    product?.productzname || i18n.t('lbl.qrFormula', 'QR formula'),
    COLORNAME_MAX_LENGTH
  );

  const missingColorants = [];
  const cntsinitem = colorantComponents.map((component) => {
    const cnt = findColorantByCode(cntcodemap, component.name);
    if (!cnt) {
      missingColorants.push(component.name);
      return null;
    }

    return {
      cntcode: cnt.cntcode,
      cntid: cnt.cntid,
      volume: componentQuantityAsVolume(component, cnt.specificgravity),
      cumulativevolume: null,
      originalvolume: null,
      cntcost: null,
    };
  });

  if (missingColorants.length) {
    throw adlerError(
      'msg.barcodeInvalidColorantCode',
      'Colorants not found: {{codes}}',
      { codes: missingColorants.join(', ') }
    );
  }

  if (cntsinitem.some((cnt) => !Number.isFinite(cnt.volume))) {
    throw adlerError(
      'msg.barcodeInvalidColorantQuantity',
      'One or more colorant quantities are invalid'
    );
  }

  const result = {
    itemid: null,
    orderid: null,
    parentitemid: null,
    zoneid,
    ...adlerColour(adlerQr),
    rgb: null,
    reflectance: null,
    colourid: null,
    productname,
    productcode,
    productid: product?.productid ?? null,
    basecode: truncate(
      base?.basecode || baseComponent.name,
      BASECODE_MAX_LENGTH
    ),
    baseid: base?.baseid ?? null,
    basevolume: baseVolume || null,
    cansizecode:
      can?.cansizecode ||
      truncate(
        `${baseComponent.quantityText} ${baseComponent.unit}`,
        CANSIZECODE_MAX_LENGTH
      ),
    cansizeid: can?.cansizeid ?? null,
    canid: can?.canid ?? null,
    nominalamount: can?.nominalamount ?? null,
    gravimetricnominal: can?.gravimetric || false,
    ncans: 1,
    discount: 0,
    basepriceadjustment: 0,
    cntpriceadjustment: 0,
    fcomment: null,
    icomment: null,
    source: SOURCE_USER,
    status: NUMERIC_ORDER_STATUS[ORDER_STATUS_WAITING],
    itemnotes: null,
    hidden: null,
    cardname: null,
    colourcardid: null,
    colourcodetype: null,
    scaledby: 1,
    feedback: null,
    barcode: adlerQr.originalPayload,
    localorder: {
      orderid: null,
      siteid,
      customerid: null,
      localuserid: null,
      orderstatus: NUMERIC_ORDER_STATUS[ORDER_STATUS_WAITING],
      orderdate: now,
      modificationdate: now,
      notes: null,
      clientid: null,
      priority: null,
      zoneid,
    },
    cntsinitem,
    dispenses: [],
  };

  console.log('Built order item from QR:', result);
  return result;
}
